import { Schema } from 'leva/dist/declarations/src/types';
import { buttonGroup, folder, LevaInputs } from 'leva';
import { StateMachineInputType } from '@rive-app/react-webgl2';
import { StateMachineInput } from 'types/rive';
import * as react_webgl2 from '@rive-app/react-webgl2';
import * as react_canvas from '@rive-app/react-canvas';
import { MutableRefObject } from 'react';

export function buildName(propertyName: string, level: number, suffix: string) {
  return `${propertyName}_${level}_${suffix}`;
}

export function buildLegacyInputs(
  inputs:
    | react_webgl2.StateMachineInput[]
    | react_canvas.StateMachineInput[]
    | StateMachineInput[]
): Schema {
  const props: Schema = {};

  inputs.forEach((input) => {
    const label = input.name.toLowerCase().replaceAll(' ', '_');
    switch (input.type) {
      case StateMachineInputType.Boolean:
        {
          props[label] = {
            label: input.name,
            value: input.value as boolean,
            transient: false,
            onChange: (value) => (input.value = value),
          };
        }
        break;
      case StateMachineInputType.Trigger:
        {
          props[label] = buttonGroup({
            label: input.name,
            opts: { Fire: () => input.fire() },
          });
        }
        break;
      case StateMachineInputType.Number:
        {
          props[label] = {
            label: input.name,
            value: input.value as number,
            transient: false,
            step: 1,
            onChange: (value) => (input.value = value),
          };
          props[`${label}-buttons`] = buttonGroup({
            label: '',
            opts: {
              '[ - ]': () => (input.value as number)--,
              '[ + ]': () => (input.value as number)++,
            },
          });
        }
        break;
    }
  });

  return props;
}

export function buildViewModelProperties(
  viewModelInstance:
    | react_webgl2.ViewModelInstance
    | react_canvas.ViewModelInstance,
  level: number,
  instanceName: string,
  levaStatus: MutableRefObject<boolean>
): Schema {
  const props: Schema = {};
  let ind = 0;
  viewModelInstance.properties.forEach((property) => {
    const path = buildName(property.name, level, instanceName);
    switch (property.type.toString()) {
      case 'number':
        {
          const numberProp = viewModelInstance.number(property.name);
          if (numberProp) {
            props[path] = {
              label: property.name,
              value: numberProp.value,
              type: LevaInputs.NUMBER,
              step: 1,
              onChange: (value: number) => {
                if (levaStatus.current === true) {
                  numberProp.value = value;
                }
              },
            };
            props[`${path}-buttons`] = buttonGroup({
              label: '',
              opts: {
                '[ - ]': () => numberProp.value--,
                '[ + ]': () => numberProp.value++,
              },
            });
          }
        }
        break;
      case 'string':
        {
          const stringProp = viewModelInstance.string(property.name);
          if (stringProp) {
            props[path] = {
              label: property.name,
              value: stringProp.value,
              type: LevaInputs.STRING,
              rows: true,
              onChange: (value: string) => {
                if (levaStatus.current === true) {
                  stringProp.value = value;
                }
              },
            };
          }
        }
        break;
      case 'boolean':
        {
          const boolProp = viewModelInstance.boolean(property.name);
          if (boolProp) {
            props[path] = {
              label: property.name,
              value: boolProp.value,
              type: LevaInputs.BOOLEAN,
              onChange: (value: boolean) => {
                if (levaStatus.current === true) {
                  boolProp.value = value;
                }
              },
            };
          }
        }
        break;
      case 'enumType':
        {
          const enumProp = viewModelInstance.enum(property.name);
          if (enumProp) {
            const values = enumProp.values;
            const currentValue = enumProp.value || '- Select -';
            props[path] = {
              label: property.name,
              value: currentValue,
              options: values,
              onChange: (value: string) => {
                if (levaStatus.current === true) {
                  enumProp.value = value;
                }
              },
            };
          }
        }
        break;
      case 'color':
        {
          const colorProp = viewModelInstance.color(property.name);
          if (colorProp) {
            const value = colorProp.value;
            const a = (value >> 24) & 0xff;
            const r = (value >> 16) & 0xff;
            const g = (value >> 8) & 0xff;
            const b = value & 0xff;
            props[path] = {
              label: property.name,
              r: r,
              g: g,
              b: b,
              a: a / 255,
              onChange: (value: any) => {
                if (levaStatus.current === true) {
                  colorProp.rgba(value.r, value.g, value.b, value.a * 255);
                }
              },
            };
          }
        }
        break;
      case 'trigger':
        {
          const triggerProp = viewModelInstance.trigger(property.name);
          if (triggerProp) {
            props[path] = buttonGroup({
              label: property.name,
              opts: {
                ['trigger']: () => {
                  triggerProp.trigger();
                },
              },
            });
          }
        }
        break;
      case 'viewModel':
        {
          const viewModelProp = viewModelInstance.viewModel(property.name);
          if (viewModelProp) {
            props[property.name] = folder(
              buildViewModelProperties(
                viewModelProp,
                level + 1,
                `${instanceName}_${ind}`,
                levaStatus
              )
            );
          }
        }
        break;
    }
    ind++;
  });
  return props;
}
